// A2-recovery (upload-таймаут): когда POST /uploads отвалился без ответа сервера
// (медленная синхронная запись на диск VPS под нагрузкой/swap), загрузка могла
// успешно создаться на бэке. Чтобы не показывать ложную ошибку, перечитываем список
// и пытаемся найти ИМЕННО нашу только что отправленную загрузку.
//
// Матчим по identity (имя+размер+mime), а не по «верхнему в списке» —
// IndexUploadController сортирует только created_at desc, и без identity-фильтра
// можно было бы увести юзера в чужой/старый активный анализ (deep-link /upload,
// застрявший processing, параллельная попытка). created_at отсекает старьё с
// допуском на расхождение часов клиент/сервер.
//
// Backend dedup (content_hash unique среди processing/awaiting/success) гарантирует,
// что одновременно «в полёте» не может быть двух загрузок с одинаковым содержимым —
// поэтому точного identity-матча достаточно для однозначности.

import type { UploadListItem, UploadStatus } from '@/api/types';

export type PickedFileMeta = {
  name: string;
  size: number;
  type: string;
};

// «В полёте» — загрузка ещё обрабатывается или ждёт ревью пользователя.
const IN_FLIGHT_STATUSES: ReadonlyArray<UploadStatus> = ['processing', 'awaiting_user_review'];

// Допуск на расхождение часов клиент/сервер при сравнении created_at со startedAt.
// Мобильные часы обычно синхронны (NTP), но 5 минут страхуют от ложного отсева.
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 60_000;

/**
 * Выбирает из списка загрузок пользователя ту, что соответствует только что
 * отправленному файлу и всё ещё «в полёте». Pure — покрыто юнит-тестами.
 *
 * @param uploads   список (ожидается newest-first из IndexUploadController)
 * @param picked    метаданные отправленного файла (name/size/type)
 * @param startedAtMs  Date.now() в момент старта загрузки (клиентские часы)
 * @returns подходящая загрузка либо null
 */
export function selectRecoverableUpload(
  uploads: UploadListItem[],
  picked: PickedFileMeta,
  startedAtMs: number,
): UploadListItem | null {
  const minCreatedMs = startedAtMs - CLOCK_SKEW_TOLERANCE_MS;

  for (const u of uploads) {
    if (!IN_FLIGHT_STATUSES.includes(u.status)) continue;
    if (u.original_filename !== picked.name) continue;
    if (u.file_size !== picked.size) continue;
    if (u.mime_type !== picked.type) continue;
    if (new Date(u.created_at).getTime() < minCreatedMs) continue;
    return u; // список newest-first → первый матч и есть самый свежий
  }

  return null;
}
